import {html, HTMLTemplateResult, nothing} from "lit";
import {AbstractO2dElement} from "@o2d/shared";
import {customElement} from "lit/decorators/custom-element.js";
import {state} from "lit/decorators/state.js";
import {get, translate} from "lit-translate";
import {container} from "tsyringe";

import "../../components/my-organizer/my-organizer-assigned-column.component";
import "../../components/my-organizer/my-organizer-boxes-grid.component";
import {PersonalLabelRepository} from "../../domain/repositories/personal-label.repository";
import {CommandBus} from "../../services/command-bus.service";
import {CreatePersonalLabelCommand} from "../../commands/create-personal-label.command";
import type {PersonalLabel} from "../../domain/entities/personal-label.entity";
import {UiHelper} from "../../helpers/ui.helper";
import {NOTIFICATION_DURATION} from "../../constants";
// The literal event name in the template below ("my-organizer.label-deleted")
// must stay in sync with `MY_ORGANIZER_LABEL_DELETED_EVENT`'s own value —
// Lit's `@eventname=` binding needs a STATIC string in the template, so the
// constant itself can't be interpolated in here the way `MY_ORGANIZER_ISSUE_*`
// event names are on `window.dispatchEvent`/`addEventListener` calls elsewhere.
import type {MyOrganizerLabelDeletedEventDetail} from "../../components/my-organizer/my-organizer-drag-context";

/** Default text for every newly created personal-label column — see the
 *  view's own doc comment. Not translated: it's the initial VALUE of a
 *  User-owned, later-renameable data field (docs/feature-plans/0026-
 *  personal-labels-issue-data/), not app chrome. */
const DEFAULT_LABEL_TEXT = 'Meine Box';

/**
 * Top-level "My Organizer" mode landing view (personal, per-User todo
 * organization — labels/time-cluster/date, see docs/feature-plans/0026-
 * personal-labels-issue-data/). Mode-switch entry: ha-mode-switch.component.ts
 * (sidebar label "My Organizer"), mode derivation: mode-router.controller.ts.
 *
 * One column showing every Issue assigned to the current User that is open
 * or on a reminder (`<my-organizer-assigned-column>`, fixed, outside the
 * grid below), plus one `<my-organizer-label-column>` PER personal label the
 * User owns — starting with exactly one ("Meine Box", auto-created on first
 * visit if the User has none yet), and growing as more are created via the
 * "Neue Box anlegen" text link in the header (Anke, 2026-09-06: "eine neue
 * Spalte rechts neben den bestehenden entstehen" — originally a 3-dot menu,
 * dropped in favor of a plain link the same day: "bitte nimm das
 * drei-punkte menu weg und setze den link... direkt als text oben hinein").
 *
 * The boxes THEMSELVES render inside `<my-organizer-boxes-grid>` (Anke,
 * 2026-09-10/13: "die Boxen ... beliebig im Grid verschiebbar und
 * skalierbar" — freely positionable/resizable, like the Linkview grid, and
 * autosaved per User) — see that component's own doc comment for the
 * Gridstack wiring and persistence flow; this view only owns the label
 * LIST (`_labels`), not their layout.
 *
 * Every label text must be unique per owner (DB-level partial unique index
 * `UNIQ_PL_OWNER_LABEL`, enforced again in `CreatePersonalLabelCommandHandler`)
 * — `_computeDefaultLabelText()` picks the first free "Meine Box"/"Meine Box
 * N" so a second click doesn't just fail against the first one (Anke:
 * "weil die box den gleichen Namen hat wie die andere... einfach eine Ziffer
 * ranhängen"). Removing labels is still a later step; renaming is NOT — see
 * `<my-organizer-label-column>`'s own doc comment for the inline-rename flow.
 * Issues are assigned/
 * moved between columns by dragging them — see `<my-organizer-label-column>`'s
 * own doc comment for the drop/persist flow, including how a column removes
 * an Issue that just got Moved to a DIFFERENT column.
 *
 * Extends `AbstractO2dElement` (`@o2d/shared`), not the legacy
 * `AbstractLitElement` — mandatory for new components per ADR-007. Light DOM
 * comes from that base class already (no separate opt-in needed).
 */
@customElement('o2d-personal-organizer-view')
export class PersonalOrganizerView extends AbstractO2dElement {

    @state()
    private _labels: PersonalLabel[] = [];

    /**
     * Bug report (Anke, 2026-09-15): on reload, the boxes grid visibly
     * "reshuffled" before settling — `<my-organizer-boxes-grid>` used to be
     * rendered unconditionally with `_labels` starting `[]`, so it mounted
     * an empty Gridstack grid on the FIRST render and only received the real,
     * already-positioned boxes on a SECOND render once `_ensureLabels()`
     * resolved. Gating the grid behind this flag instead means it mounts
     * exactly once, already holding the final label list — no more
     * empty-grid-then-repopulate flash. Mirrors the same
     * render-nothing-until-ready idiom `ha-app.component.ts` already uses
     * for `_translationsReady`.
     */
    @state()
    private _labelsReady = false;

    public override connectedCallback(): void {
        super.connectedCallback();
        this._ensureLabels();
    }

    private async _ensureLabels(): Promise<void> {
        try {
            const repository = container.resolve(PersonalLabelRepository);
            const existing = await repository.findAllMine();
            if (existing.length > 0) {
                this._labels = existing;
                return;
            }

            const label = await this._createLabel();
            if (label) {
                this._labels = [label];
            }
        } finally {
            this._labelsReady = true;
        }
    }

    /**
     * The first free "Meine Box" / "Meine Box 2" / "Meine Box 3" / ... text
     * against the CURRENTLY LOADED labels — a label's text must be unique
     * per owner (see `render()`'s own doc comment), and the default text is
     * always the same literal, so a second (third, ...) box would otherwise
     * always collide with an existing one. Checked client-side against
     * already-loaded `_labels` purely to avoid the common case without a
     * round-trip; the backend's own check (and DB unique index) stays the
     * actual source of truth (e.g. against a label created in another tab
     * since this page loaded).
     */
    private _computeDefaultLabelText(): string {
        const existingTexts = new Set(this._labels.map(label => label.label));
        if (!existingTexts.has(DEFAULT_LABEL_TEXT)) {
            return DEFAULT_LABEL_TEXT;
        }
        let suffix = 2;
        while (existingTexts.has(`${DEFAULT_LABEL_TEXT} ${suffix}`)) {
            suffix++;
        }
        return `${DEFAULT_LABEL_TEXT} ${suffix}`;
    }

    /**
     * "Neue Box anlegen" (Anke, 2026-09-06) — appends the new column to the
     * RIGHT of the existing ones (`[...this._labels, label]`, never
     * re-sorted: creation order IS display order, there's no separate
     * position concept for label COLUMNS themselves, unlike the Issue ROWS
     * inside one).
     */
    private async _createLabel(): Promise<PersonalLabel | null> {
        const repository = container.resolve(PersonalLabelRepository);
        const label = await repository.create(this._computeDefaultLabelText());
        const dispatched = await container.resolve(CommandBus).dispatchCommand(
            new CreatePersonalLabelCommand('personal-label', label.uid, label)
        );

        if (!dispatched) {
            console.error('[o2d-personal-organizer-view] failed to create a new personal label');
            // Review-Fund (2026-09-06, Linse A): ein fehlgeschlagenes Anlegen
            // wirkte für die Nutzerin sonst einfach wirkungslos (kein Toast,
            // nur console.error) — z.B. bei einem Namens-Race mit einem
            // parallelen Tab. Ein Toast statt einer stillen Rückgabe von
            // `null` gibt sichtbares Feedback, egal ob dieser Aufruf vom
            // initialen Auto-Create (`_ensureLabels()`) oder vom "Neue Box
            // anlegen"-Klick (`_onCreateBoxClick()`) kam — beide teilen sich
            // diese eine Methode.
            UiHelper.notify({
                level: 'error',
                // `get()`, not `translate()` — `ToastMessage.title` is a plain
                // `string` field, not a Lit template binding (mirrors the
                // same `get()`-vs-`translate()` distinction already made for
                // `PopoverListItem.label` elsewhere in this codebase).
                title: get('personal-organizer.boxes-menu.create-failed-title'),
                content: html`<p>${translate('personal-organizer.boxes-menu.create-failed-content')}</p>`,
                closable: true,
                duration: NOTIFICATION_DURATION,
            });
            return null;
        }

        return label;
    }

    private async _onCreateBoxClick(): Promise<void> {
        const label = await this._createLabel();
        if (label) {
            this._labels = [...this._labels, label];
        }
    }

    /** A `<my-organizer-label-column>` tells us it just deleted itself (see
     *  its own doc comment on `_onDeleteClick()`) — removing it from
     *  `_labels` is this view's job, not the column's own: it has no way to
     *  un-render itself out of a list it doesn't own. */
    private _onLabelDeleted(event: CustomEvent<MyOrganizerLabelDeletedEventDetail>): void {
        const {labelUid} = event.detail;
        this._labels = this._labels.filter(label => label.uid !== labelUid);
    }

    public render(): HTMLTemplateResult {
        return html`
            <div class="my-organizer">
                <my-organizer-assigned-column></my-organizer-assigned-column>
                <div class="my-organizer-boxes">
                    <div class="my-organizer-boxes__header">
                        <span class="my-organizer-boxes__title">${translate('personal-organizer.nav.boxes')}</span>
                        <button type="button"
                                class="my-organizer-boxes__create-link"
                                @click=${() => this._onCreateBoxClick()}
                        >${translate('personal-organizer.boxes-menu.create')}</button>
                    </div>
                    ${this._labelsReady ? html`
                        <my-organizer-boxes-grid .labels=${this._labels}
                            @my-organizer.label-deleted=${(event: CustomEvent<MyOrganizerLabelDeletedEventDetail>) => this._onLabelDeleted(event)}
                        ></my-organizer-boxes-grid>
                    ` : nothing}
                </div>
            </div>
        `;
    }

}
